(() => {
  const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
  const MONTHS_SHORT = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
  const WEEKDAYS = ['Mo','Di','Mi','Do','Fr','Sa','So'];
  const WEEKDAYS_LONG = ['Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag','Sonntag'];

  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const fromISO = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  };
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const sameDay = (a, b) => a && b && toISO(a) === toISO(b);

  class GiftCardDatePicker {
    constructor(input) {
      this.input = input;
      this.today = startOfDay(new Date());
      this.selected = fromISO(input.value);
      this.view = new Date((this.selected || this.today).getFullYear(), (this.selected || this.today).getMonth(), 1);
      this.build();
    }

    build() {
      const input = this.input;
      const id = input.id || `gcdate-${Math.random().toString(36).slice(2, 8)}`;

      this.root = document.createElement('div');
      this.root.className = 'lhc-gcdate';

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'lhc-gcdate__field';
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.innerHTML =
        '<span class="lhc-gcdate__value"></span>' +
        '<svg class="lhc-gcdate__caret" viewBox="0 0 256 256" width="20" height="20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M216.49,104.49l-80,80a12,12,0,0,1-17,0l-80-80a12,12,0,0,1,17-17L128,159l71.51-71.52a12,12,0,0,1,17,17Z"/></svg>';

      this.panel = document.createElement('div');
      this.panel.className = 'lhc-gcdate__cal';
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-label', 'Datum wählen');
      this.panel.id = `${id}-cal`;
      this.panel.hidden = true;
      this.panel.innerHTML =
        '<div class="lhc-gcdate__head">' +
        '<button class="lhc-gcdate__nav" type="button" data-nav="prev" aria-label="Vorheriger Monat">&lsaquo;</button>' +
        '<span class="lhc-gcdate__month" aria-live="polite"></span>' +
        '<button class="lhc-gcdate__nav" type="button" data-nav="next" aria-label="Nächster Monat">&rsaquo;</button>' +
        '</div>' +
        `<div class="lhc-gcdate__weekdays" aria-hidden="true">${WEEKDAYS.map((w) => `<span>${w}</span>`).join('')}</div>` +
        '<div class="lhc-gcdate__grid"></div>' +
        '<button class="lhc-gcdate__clear" type="button">Kein Termin — sofort senden</button>';

      input.hidden = true;
      input.setAttribute('aria-hidden', 'true');
      input.tabIndex = -1;
      input.parentNode.insertBefore(this.root, input);
      this.root.append(this.trigger, this.panel, input);

      this.valueEl = this.trigger.querySelector('.lhc-gcdate__value');
      this.monthEl = this.panel.querySelector('.lhc-gcdate__month');
      this.gridEl = this.panel.querySelector('.lhc-gcdate__grid');

      this.trigger.addEventListener('click', () => this.toggle());
      this.panel.addEventListener('click', (e) => this.onPanelClick(e));
      document.addEventListener('click', (e) => {
        if (!this.root.contains(e.target)) this.close();
      });
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) {
          this.close();
          this.trigger.focus();
        }
      });

      this.renderValue();
      this.renderGrid();
    }

    renderValue() {
      if (this.selected) {
        const d = this.selected;
        this.valueEl.textContent = `${WEEKDAYS_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()}. ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
        this.trigger.classList.add('is-set');
      } else {
        this.valueEl.textContent = 'Sofort nach dem Kauf senden';
        this.trigger.classList.remove('is-set');
      }
    }

    renderGrid() {
      this.monthEl.textContent = `${MONTHS[this.view.getMonth()]} ${this.view.getFullYear()}`;
      const first = new Date(this.view.getFullYear(), this.view.getMonth(), 1);
      const offset = (first.getDay() + 6) % 7;
      const days = new Date(this.view.getFullYear(), this.view.getMonth() + 1, 0).getDate();

      const cells = [];
      for (let i = 0; i < offset; i += 1) cells.push('<span class="lhc-gcdate__pad"></span>');
      for (let day = 1; day <= days; day += 1) {
        const date = new Date(this.view.getFullYear(), this.view.getMonth(), day);
        const past = date < this.today;
        const cls = ['lhc-gcdate__day'];
        if (sameDay(date, this.selected)) cls.push('is-selected');
        if (sameDay(date, this.today)) cls.push('is-today');
        cells.push(
          `<button type="button" class="${cls.join(' ')}" data-date="${toISO(date)}"${past ? ' disabled' : ''}>${day}</button>`
        );
      }
      this.gridEl.innerHTML = cells.join('');

      const prev = this.panel.querySelector('[data-nav="prev"]');
      prev.disabled = this.view.getFullYear() === this.today.getFullYear() && this.view.getMonth() === this.today.getMonth();
    }

    onPanelClick(e) {
      const nav = e.target.closest('[data-nav]');
      if (nav) {
        this.view = new Date(this.view.getFullYear(), this.view.getMonth() + (nav.dataset.nav === 'next' ? 1 : -1), 1);
        this.renderGrid();
        return;
      }
      if (e.target.closest('.lhc-gcdate__clear')) {
        this.commit(null);
        return;
      }
      const day = e.target.closest('[data-date]');
      if (day && !day.disabled) this.commit(fromISO(day.dataset.date));
    }

    commit(date) {
      this.selected = date;
      this.input.value = date ? toISO(date) : '';
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.renderValue();
      this.renderGrid();
      this.close();
      this.trigger.focus();
    }

    toggle() {
      if (this.panel.hidden) this.open();
      else this.close();
    }

    open() {
      this.view = new Date((this.selected || this.today).getFullYear(), (this.selected || this.today).getMonth(), 1);
      this.renderGrid();
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  const SELECTOR = 'input[type="date"][name="properties[Send on]"]';

  const enhance = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll(SELECTOR).forEach((input) => {
      if (input.dataset.lhcGcdate) return;
      input.dataset.lhcGcdate = 'true';
      const picker = new GiftCardDatePicker(input);
      input.addEventListener('lhc:gcdate:sync', () => {
        picker.selected = fromISO(input.value);
        picker.renderValue();
      });
    });
  };

  const init = () => enhance(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('recipient:toggle', (event) => {
    enhance(document);
    const form = event.target;
    if (!form || !form.querySelectorAll) return;
    form.querySelectorAll(SELECTOR).forEach((input) => {
      input.dispatchEvent(new CustomEvent('lhc:gcdate:sync'));
    });
  });

  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) {
            enhance(node.parentNode || document);
            return;
          }
          if (node.querySelector && node.querySelector(SELECTOR)) {
            enhance(node);
            return;
          }
        }
      }
    });
    const observe = () => observer.observe(document.body, { childList: true, subtree: true });
    if (document.body) observe();
    else document.addEventListener('DOMContentLoaded', observe);
  }
})();
